import React from 'react';

// Typography renders the shared type scale: font-size, font-weight,
// line-height, letter-spacing, text-transform - the metrics defined per
// row in config/_typography.scss (the single source of truth for the
// actual CSS values these classes apply). It is deliberately not
// responsible for color, margin/spacing, or other layout concerns; use
// `className` for those one-off overrides.
//
// If the same override className keeps showing up paired with the same
// `style` across multiple pages/components (e.g. a recurring color +
// spacing combo), that's a signal the override has become a de facto
// style of its own - add it as a new TypographyStyle/STYLE_CONFIG entry
// (see "eyebrow-badge") instead of continuing to duplicate the className
// at every call site.

// Keys match the Figma type scale rows (see config/_typography.scss, the
// single source of truth for the actual CSS values these classes apply).
export type TypographyStyle =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'paragraph'
  | 'paragraph-small-1'
  | 'paragraph-small-2'
  | 'microcopy'
  | 'eyebrow'
  | 'eyebrow-badge'
  | 'card-title'
  | 'quote'
  | 'quote-attribution'
  | 'label-small'
  | 'stats-lg';

// Only the tags this component actually renders - kept narrow because the
// full `keyof React.JSX.IntrinsicElements` union includes SVG elements
// whose prop types aren't compatible with React.HTMLAttributes<HTMLElement>.
export type TypographyTag =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'blockquote'
  | 'cite';

const STYLE_CONFIG: Record<
  TypographyStyle,
  { tag: TypographyTag; className: string }
> = {
  h1: { tag: 'h1', className: 'page-h1' },
  h2: { tag: 'h2', className: 'page-h2' },
  h3: { tag: 'h3', className: 'page-h3' },
  h4: { tag: 'h4', className: 'page-h4' },
  h5: { tag: 'h5', className: 'page-h5' },
  h6: { tag: 'h6', className: 'page-h6' },
  paragraph: { tag: 'p', className: 'page-paragraph' },
  'paragraph-small-1': { tag: 'p', className: 'page-paragraph-small-1' },
  'paragraph-small-2': { tag: 'p', className: 'page-paragraph-small-2' },
  microcopy: { tag: 'span', className: 'page-microcopy' },
  eyebrow: { tag: 'span', className: 'page-eyebrow' },
  'eyebrow-badge': { tag: 'span', className: 'page-eyebrow-badge' },
  'card-title': { tag: 'h3', className: 'page-card-title' },
  quote: { tag: 'blockquote', className: 'page-quote' },
  'quote-attribution': { tag: 'cite', className: 'page-quote-attribution' },
  'label-small': { tag: 'span', className: 'page-label-small' },
  'stats-lg': { tag: 'span', className: 'page-stats-lg' },
};

export interface TypographyProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  'style'
> {
  /** Which row of the type scale to render (see config/_typography.scss). */
  style: TypographyStyle;
  /** Overrides the default element for this style, e.g. style="h2" as="div". */
  as?: TypographyTag;
  /**
   * One-off overrides only (color, margin/spacing, etc.) - never font-size,
   * font-weight, line-height, or letter-spacing; those belong in `style`.
   * If a className keeps recurring with the same `style`, promote it to a
   * new TypographyStyle instead of repeating it at every call site.
   */
  className?: string;
  children?: React.ReactNode;
}

// `style` is deliberately a typed enum here, not the DOM style object -
// this component is only about which row of the shared type scale to use,
// never inline CSS. Use `className` for one-off layout tweaks (see the
// TypographyProps.className doc above for what belongs there vs. in `style`).
const Typography: React.FC<TypographyProps> = ({
  style,
  as,
  className,
  children,
  ...props
}) => {
  const { tag, className: styleClassName } = STYLE_CONFIG[style];
  const Tag = as || tag;

  if (process.env.NODE_ENV !== 'production' && as === tag) {
    console.warn(
      `Typography: as="${as}" is redundant - style="${style}" already renders a <${tag}> by default.`
    );
  }

  return (
    <Tag {...props} className={`${styleClassName} ${className || ''}`.trim()}>
      {children}
    </Tag>
  );
};

export default Typography;
